import { IModals, IRates, ITickerData, User, Wallet } from '@App/interfaces/CommonInterfaces'

import { Order, StopLoss, Trade } from '@Redux/slices/trade/trades/types'
import { Watchlist } from '@Redux/slices/watchlist/slice'

import {AlertType} from "@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/types";

import {ICountry, ILanguage} from '../slices/config/interfaces'

export interface ITVHistoryFeed {
  provider: string
  all_symbol_url: string
  history_url: string
  api_key: string
}

export interface IConfigState {
  userServiceWsApi: string
  proxyServiceWsApi: string
  frontServiceWsApi: string
  publicServiceWsApi: string
  coinRateApi: string
  currencies: IConfigCurrency[]
  system_currencies: IConfigSystemCurrency[]
  shared_countries: ICountry[]
  markets: IConfigMarket[]
  favoriteMarkets: IConfigMarket[]
  activeFavoriteMarket: IConfigMarket | null
  history_feeds: ITVHistoryFeed[]
  languages: ILanguage[] // todo: type this
  isConfigLoading: boolean
  is_freeze_reality_check: null | boolean
  fiats: IFiatFilter[]
}

export class ConfigMarket implements IConfigMarket {
  type: string
  description: string
  amount_precision: number
  commission: number
  cross_rate: number
  enable_in_exchange: number
  enable_in_isolated: number
  enable_in_spot: number
  enable_swap_exchange: number
  enabled: boolean
  id: number
  is_popular: number
  isolate_rate: number
  price_precision: number
  source_currency: string
  source_currency_code: string
  source_country: string
  symbol: string
  target_currency: string
  target_currency_code: string
  target_country: string
  title: string
  klines: IKline
  is_open: boolean
  mode: string
  schedules: IConfigMarketSchedule[]
  rule : IMarketRule
  country_key: string | null
  percent_change: number
  count_buy_bet: number
  count_sell_bet: number

  public constructor(raw_market: any) {
    this.type = raw_market.type
    this.amount_precision = raw_market.amount_precision
    this.commission = raw_market.commission
    this.cross_rate = raw_market.cross_rate
    this.enable_in_exchange = raw_market.enable_in_exchange
    this.enable_in_isolated = raw_market.enable_in_isolated
    this.enable_in_spot = raw_market.enable_in_spot
    this.enable_swap_exchange = raw_market.enable_swap_exchange
    this.enabled = raw_market.enabled
    this.id = raw_market.id
    this.is_popular = raw_market.is_popular
    this.isolate_rate = raw_market.isolate_rate
    this.price_precision = raw_market.price_precision
    this.source_currency = raw_market.source_currency
    this.source_currency_code = raw_market.source_currency_code
    this.symbol = raw_market.symbol
    this.target_currency = raw_market.target_currency
    this.target_currency_code = raw_market.target_currency_code
    this.title = raw_market.title
    this.klines = raw_market.klines
    this.is_open = raw_market.is_open
    this.mode = raw_market.mode
    this.schedules = raw_market.schedules
    this.rule = raw_market.rule
    this.description = raw_market.description
    this.country_key = raw_market.country_key?.toLowerCase() ?? null
    this.percent_change = raw_market.percent_change
    this.count_buy_bet = raw_market.count_buy_bet
    this.count_sell_bet = raw_market.count_sell_bet
    this.source_country = raw_market.source_country?.toLowerCase() ?? null
    this.target_country = raw_market.target_country?.toLowerCase() ?? null
  }

  enableInIsolatedMargin(): boolean {
    return this.isolate_rate > 0 && this.enable_in_isolated == 1
  }

  enableInMargin(): boolean {
    return this.cross_rate > 0
  }

  enableInSpot(): boolean {
    return this.enable_in_spot == 1
  }
}

export interface IConfigCurrency {
  code: string
  title: string
  type: string
  symbol: string
  enabled: boolean
  is_base: boolean
  deleted: boolean | undefined
  deposit_enabled: boolean
  withdrawal_enabled: boolean
  provide_enabled: boolean
  loan_enabled: boolean
}

export interface IConfigSystemCurrency {
  code: string
  title: string
  symbol: string
}

export interface IConfigMarket {
  amount_precision: number
  commission: number
  enable_in_isolated: number
  enable_in_exchange: number
  enable_in_spot: number
  enable_swap_exchange: number
  cross_rate: number
  isolate_rate: number
  enabled: boolean
  id: number
  is_popular: number
  price_precision: number
  source_currency: string
  symbol: string
  source_currency_code: string
  source_country: string | null
  target_currency: string
  target_currency_code: string
  target_country: string | null
  title: string
  enableInMargin: () => boolean
  enableInIsolatedMargin: () => boolean
  enableInSpot: () => boolean
  type: string
  klines: IKline
  is_open: boolean
  mode: string
  schedules: IConfigMarketSchedule[]
  rule: IMarketRule
  description: string
}

export interface IMarketInfo {
  id: number
  title: string
  type: string
  amount_precision: number
  price_precision: number
  source_currency: string
  target_currency: string
  spread_ask: number
  spread_bid: number
  enabled: boolean
  is_open: boolean
  mode: string
  high: number
  low: number
  description: string
  country_key: string | null
  percent_change: number
  count_buy_bet: number
  count_sell_bet: number

  schedules: IConfigMarketSchedule[]
  rule: IMarketRule
  price_range: IMarketPriceRange
}

export interface IConfigMarketSchedule {
  day_of_week: number
  start_time: string
  end_time: string
}

export interface IMarketPriceRange {
  daily: IPriceRange
  weekly: IPriceRange
  monthly: IPriceRange
  yearly: IPriceRange
}

export interface IPriceRange {
  high: number
  low: string
}

export interface IKline {
  interval: string,
  source_currency: string
  target_currency: string
  timestamp: string
  limit: number
  data: {
    update_time: number
    start_timestamp: number
    end_timestamp: number
    market: string
    interval: string
    quantity: number
    open: number
    close: number
    high: number
    low: number
    source_currency: string
    target_currency: string
  }[]
}

export interface IMarketRule {
  min_step_distance: number
  min_deal_size: number
  max_deal_size: number
  min_size_increment: number
  min_guaranteed_stop_distance: number //in percent
  min_stop_or_profit_distance: number //in percent
  max_stop_or_profit_distance: number //in percent
}

export interface IFiatFilter {
  key: string
  name: string
  value: string
}

export enum MarketMode {
  ONLY_LONG = 'ONLY_LONG',
  ONLY_SHORT = 'ONLY_SHORT',
  REGULAR = 'REGULAR',
  CLOSED_ONLY = 'CLOSED_ONLY',
  OFFLINE = 'OFFLINE'
}


export enum MarketFilter {
  Gainer = 'gainer',
  Loser = 'loser',
  MostTraded = 'most_traded',
  All = 'all',
  Crypto = 'crypto',
  MostVolatile = 'most_volatile',
  BaseMetals = 'base_metals',
  Energies = 'energies',
  PreciousMetals = 'precious_metals',
  SoftCommodities = 'soft_commodities',
  CommodityFutures = 'commodity_futures',
  Indexes = 'indexes',
}

export const TAB_TO_FILTER_MAP: Record<string, string> = {
  top_risers: MarketFilter.Gainer,
  top_fallers: MarketFilter.Loser,
  most_traded: MarketFilter.MostTraded,
  all: MarketFilter.All,
  crypto: MarketFilter.Crypto,
  most_volatile: MarketFilter.MostVolatile,
  base_metals: MarketFilter.BaseMetals,
  energies: MarketFilter.Energies,
  precious_metals: MarketFilter.PreciousMetals,
  soft_commodities: MarketFilter.SoftCommodities,
  commodity_futures: MarketFilter.CommodityFutures,
  indexes: MarketFilter.Indexes,
}

export const mapTabToBackendFilter = (tab: string): string => {
  if (TAB_TO_FILTER_MAP[tab]) {
    return TAB_TO_FILTER_MAP[tab]
  }

  return tab
}

export const canBuy = (market: IConfigMarket): boolean => {
  if (!market.mode) return true

  return market.mode === MarketMode.ONLY_LONG || market.mode === MarketMode.REGULAR
}

export const canSell = (market: IConfigMarket): boolean => {
  if (!market.mode) return true

  return market.mode === MarketMode.ONLY_SHORT || market.mode === MarketMode.REGULAR
}

// Notification

export interface INotification {
  id: string
  message: string
  description?: string
  status: 'default' | 'success' | 'error' | 'info' | 'warning' | ''
  isResolved?: boolean
}

export interface ISimpleNotify {
  id: string
  message: string
  status: string
}

// common connection

export interface IFrontServiceConnection {
  status: boolean | undefined
  message: string | undefined
  socket_status: 'closed' | 'ready'
}

// currency service

export interface IOrderBookOffer {
  id: number
  price: number
  quantity?: number
}

interface IOrderBookByMarkets {
  [key: string]: IOrderBookByMarket
}

interface IOrderBookByMarket {
  asks: any
  bids: any
}

export interface ITradesEntity {
  is_buyer: boolean
  price: number
  quantity: number
  source_currency: string
  target_currency: string
  timestamp: number

  getTime(): string
}

interface ITradesHistoryByMarkets {
  [key: string]: ITradesHistoryByMarket
}

interface ITradesHistoryByMarket {
  source_currency: string
  target_currency: string
  trades: ITradesEntity[]
  time: number
}

export interface IPublicServiceConnection {
  rates_ticker: ITickerData
  rates: IRates
  order_book_by_market: IOrderBookByMarkets
  trades_history_by_market: ITradesHistoryByMarkets
  markets: IConfigMarket[]
  market_info?: IMarketInfo
  klinesById: Record<number, IKline>
  socket_status: 'closed' | 'ready'
  last_selected_tab: string
}

export interface IFrontServiceConnectionSlice {
  socket_status: string
}

export enum WalletTypes {
  TYPE_COIN = 'coin',
  TYPE_REAL = 'real',
  TYPE_SPOT = 'spot',
  TYPE_2P2 = 'p2p',
  TYPE_MARGIN_CROSS = 'margin_cross',
  TYPE_MARGIN_ISOLATED = 'margin_isolated',
  TYPE_P2P = 'p2p',
}

// proxy service
export interface IWallet {
  amount: number
  currency: string
  type: WalletTypes
  enabled: number
  id: number
}

export interface IProxyServiceMessage {
  event: string
  status: number
  payload: {
    user?: User
    wallet?: Wallet
    wallet_id?: number
    orders?: Order[]
    trades?: Trade[]
    alerts?: AlertType[]
    watchlists?: Watchlist[]
    watchlist_key?: string
  }
}

export interface IProxyServiceConnection {
  socket_status: string
  status?: boolean
  message?: string | undefined
}

// user

export interface IUser {
  login: boolean
  user_id: number
  username: any | string | null
  user_info: User | null
  chartSettings: {
    chartInterval: string
    chartType: string
  }
  is_loading: boolean
  check_load: boolean
  theme: string
  wallets: Wallet[]
  curr_wallet?: Wallet|null
  favorite_ticker_id: []
  is_success: boolean
  required_two_fa: boolean
  user_agent?: string
  user_session?: string
  ip?: string
  modals?: IModals
  realityCheckData: any | null
  realityCheckLoading: boolean
  is_readonly_reality_check: boolean
  termsAcceptedAt: null | string
  player_limits: []
  session_timeout: any | null
  exclude_period: any | null
  exclude_kyc: boolean
  is_readonly: boolean
  bot: boolean
  reality_check_period: any | null
  documentRequests: any
  kyc_documents_status_count: any | null
  documentFiles: any | null
}

export interface IExchangeRequestMessage {
  message: string
  token: string
  user_id: number
  amount: number | string
  source_currency: string
  target_currency: string
  request_id: string
  wallet_type: string
}

export interface ISpotOrderRequestMessage {
  token: string
  user_id: number | null
  amount: number | null
  market_id: number
  action: string
  rate: number | null
  request_id: string
  trade_id: number
  wallet_id: number
  type: 'open' | 'close' | 'change' | 'cancel'
  trade_type: 'order' | 'trade'
  price_in_wallet_currency?: number
  wallet_value?: number
  price?: number
  trigger_price_active?: boolean
  trigger_price?: number | null
  stop_loss_type?: StopLoss | null
  stop_loss_amount?: number | null
  stop_loss_distance?: number | null
  stop_loss_distance_percent?: number | null
  stop_loss_level?: number | null
  take_profit_active?: boolean
  take_profit_amount?: number | null
  take_profit_distance?: number | null
  take_profit_distance_percent?: number | null
  take_profit_level?: number | null
  cancellation_date?: string | null | undefined
  order_type?: 'stop' | 'limit' | null | undefined
  stop_loss?: IStopLossRequest
  take_profit?: ITakeProfitRequest
}

export interface ICancelOrderRequestMessage {
  token: string
  user_id: number | null
  amount: number | null
  market_id: number
  action: string
  rate: number | null
  request_id: string
  trade_id: number
  wallet_id: number
  type: 'cancel'
  trade_type: 'order' | 'trade'
}

export interface ISpotOrderRequest {
  event: string
  request_key: string
  api: string
  token: string
  user_id: number | null
  payload: {
    quantity: number
    market_id: number
    action: string
    rate: number | null
    wallet_id: number
    trade_id: number | null
    order_type: 'stop' | 'limit' | null | undefined
    type: 'open' | 'close' | 'cancel'
    trade_type: 'trade' | 'order'
    price_in_wallet_currency?: number
    wallet_value?: number
    price?: number
    trigger_price_active?: boolean
    trigger_price?: number | null
    stop_loss_type?: StopLoss | null
    stop_loss_amount?: number | null
    stop_loss_distance?: number | null
    stop_loss_distance_percent?: number | null
    stop_loss_level?: number | null
    take_profit_active?: boolean
    take_profit_amount?: number | null
    take_profit_distance?: number | null
    take_profit_distance_percent?: number | null
    take_profit_level?: number | null
    cancellation_date?: string | null | undefined
    stop_loss?: IStopLossRequest
    take_profit?: ITakeProfitRequest
  }
}

export interface ICancelOrderRequest {
  event: string
  request_key: string
  api: string
  token: string
  user_id: number | null
  payload: {
    quantity: number
    market_id: number
    action: string
    rate: number | null
    wallet_id: number
    trade_id: number | null
    type: 'cancel'
    trade_type: 'trade' | 'order'
  }
}

export interface IStopLossRequest extends ITakeProfitRequest {
  stop_loss_type: 'regular' | 'trailing' | 'guaranteed'
}

export interface ITakeProfitRequest {
  trigger_type: 'amount' | 'distance' | 'rate'
  value: number
}

export type TStatus = 'success' | 'error' | 'info' | 'warning' | 'warn' | 'open'

export interface IQueueTask {
  id: string
  type: string
  status: TStatus
}

export interface IBackNotification {
  id: string
  original_type: string
  type: string
  data: IBackNotificationData
  read_at: string
  created_at: string
}

export interface IBackNotificationData {
  title: string
  message: string
  id: string
}

export interface ISimpleEarnEntity {
  id: number
  currency_code: string
  amount: number
  amount_btc: number
  total_amount_fee?: number
  next_day_amount_fee?: number
  duration?: number
  accrue_days?: number
  apr_yesterday?: number
  apr_sum?: number
  tiers?: ISimpleEarnDurationTires[]
  paid_bonus_amount?: number
  apr: number
  is_auto_subscribe: boolean
  is_auto_subscribe_available: boolean
  status: string
  interest_next_date?: string
  interest_end_date?: string
  subscription_date?: string
  redemption_date?: string
  interest_period?: number
}

export interface ISimpleEarnDurationTires {
  id: number
  from_amount: number
  to_amount: number
  apr: number
}

export interface ISimpleEarnEntityGeneralInfo {
  total_value: number
  estimated_positions: number
  total_profit: number
  last_day_profit: number
  total_value_convert: number
  estimated_positions_convert: number
  total_profit_convert: number
  last_day_profit_convert: number
}

export interface IPriceAlertRequest {
  event: string
  api: string
  token: string
  request_type: 'request'
  payload: {
    type: 'create'
    market_id: number
    wallet_id: number
    side: string
    price: number
    note: string
  }
}

export interface IPriceAlertRequestMessage {
  token: string
  request_id: string
  market_id: number
  wallet_id: number
  side: string
  price: number
  note: string
}